import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { ChevronLeft, Calendar, Tag, User } from 'lucide-react';
import { Breadcrumb } from '../../components/common/Breadcrumb';

export const NewsDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: article, isLoading, error } = useQuery({
    queryKey: ['public-news-detail', slug],
    queryFn: () => api.get(`/public/news/${slug}`).then((res) => res.data.data),
    enabled: !!slug,
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-xs font-mono">Loading article...</div>;
  }

  if (error || !article) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-serif font-bold text-slate-900">Article Not Found</h2>
        <Link to="/news" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to News &amp; Insights
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'News & Insights', href: '/news' },
            { label: article.title },
          ]}
        />
      </div>

      <div className="space-y-4">
        <span className="text-xs font-mono uppercase tracking-widest text-brand-700 font-bold">
          {article.category}
        </span>
        <h1 className="text-3xl sm:text-5xl font-serif font-bold text-editorial-dark leading-tight">
          {article.title}
        </h1>
        <div className="flex items-center gap-4 text-xs text-slate-500 font-mono pt-2 border-b border-slate-200 pb-4">
          <span>By {article.author}</span>
          <span>&bull;</span>
          <span>{new Date(article.publishedAt || article.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      <article className="p-8 sm:p-12 rounded-2xl bg-white border border-editorial-border shadow-xs space-y-6 text-slate-700 leading-relaxed text-sm sm:text-base font-sans whitespace-pre-line">
        {article.content}
      </article>

    </div>
  );
};
